import { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useSubscription } from '../../hooks/useSubscription';
import InternalNavbar from '../../components/InternalNavbar';
import Navbar from '../../components/header';
import Footer from '../../components/footer';
import Scrollbar from '../../components/scrollbar';
import assessmentService from '../../services/assessmentService';
import { FaTrophy, FaStar, FaCheckCircle, FaCrown, FaGift, FaArrowRight, FaClipboardCheck, FaEnvelope, FaWhatsapp, FaUserPlus } from 'react-icons/fa';
import img1 from '../../images/1 (1).png';
import img3 from '../../images/3 (1).png';
import './style.css';

const LevelTestResultsPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { isAuthenticated } = useAuth();
    const { hasActiveSubscription, isDemoSubscription, isPaidSubscription } = useSubscription();

    const [testResults, setTestResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const initializeResults = async () => {
            // First, check for results from navigation state or sessionStorage
            const stateResults = location.state?.testResults;
            const storedResults = sessionStorage.getItem('level_test_results');

            if (stateResults && location.state?.fromTest) {
                setTestResults(stateResults);
                return;
            }

            if (storedResults) {
                setTestResults(JSON.parse(storedResults));
                return;
            }

            // If authenticated, try to fetch from backend
            if (isAuthenticated) {
                // Only redirect to dashboard if user has paid subscription
                if (isPaidSubscription) {
                    navigate('/dashboard', {
                        state: {
                            message: t('levelTestResults.alreadySubscribed')
                        }
                    });
                    return;
                }

                try {
                    setLoading(true);
                    const history = await assessmentService.getTestHistory();
                    if (history && history.length > 0) {
                        setTestResults(history[0]);
                    } else {
                        setError(t('levelTestResults.noResultsMessage'));
                    }
                } catch (err) {
                    console.error('Failed to fetch test results:', err);
                    setError(t('levelTestResults.loadingError'));
                } finally {
                    setLoading(false);
                }
            } else {
                // Guest with no stored results - prompt to take test
                setError(t('levelTestResults.noResultsMessage'));
            }
        };

        initializeResults();
    }, [isAuthenticated, isPaidSubscription, location.state, navigate]);

    // Dynamic navbar based on auth
    const NavComponent = isAuthenticated ? InternalNavbar : Navbar;

    const getLevelDescription = (level) => {
        const descriptions = {
            'Beginner': 'You can understand and use basic Arabic phrases.',
            'Absolute Beginner': 'Perfect starting point for learning Arabic.',
            'A1': 'Beginner - You can understand and use familiar everyday expressions.',
            'A2': 'Elementary - You can communicate in simple tasks.',
            'B1': 'Intermediate - You can deal with most travel situations.',
            'B2': 'Upper Intermediate - You can interact with fluency.',
            'C1': 'Advanced - You can express ideas fluently.',
            'C2': 'Proficient - You can understand virtually everything.'
        };
        return descriptions[level] || 'Your Arabic proficiency level has been assessed.';
    };

    const getLevelColor = (level) => {
        const colors = {
            'Beginner': '#dc3545',          // Red - Basic level
            'Absolute Beginner': '#fd7e14', // Orange - Very basic
            'Elementary': '#ffc107',        // Yellow - Elementary
            'A1': '#fd7e14',               // Orange - Basic
            'A2': '#ffc107',               // Yellow - Elementary  
            'B1': '#17a2b8',               // Cyan - Intermediate
            'Intermediate': '#17a2b8',     // Cyan - Intermediate
            'B2': '#007bff',               // Blue - Upper Intermediate
            'C1': '#28a745',               // Green - Advanced
            'Advanced': '#28a745',         // Green - Advanced
            'C2': '#6f42c1'                // Purple - Proficient
        };
        return colors[level] || '#004585'; // Default brand color
    };

    /**
     * Calculate the displayed score
     * @param {Object} results - Test results object
     * @returns {number} - Score as percentage (0-100)
     */
    const getDisplayScore = (results) => {
        if (!results) return 0;

        // If score is already a percentage (0-100), use it
        if (results.score !== undefined && results.score <= 100) {
            return Math.round(results.score);
        }

        // If we have correct_answers and total_questions, calculate percentage
        if (results.correct_answers !== undefined && results.total_questions) {
            return Math.round((results.correct_answers / results.total_questions) * 100);
        }

        // Fallback: if score > 100, it might be raw count, try to calculate
        if (results.score && results.total_questions) {
            return Math.round((results.score / results.total_questions) * 100);
        }

        // Default to 0 if we can't calculate
        return 0;
    };

    const handleDemoClick = () => {
        // Navigate to demo confirmation page with test results
        navigate('/demo-confirmation', {
            state: {
                testResults: testResults,
                fromResults: true
            }
        });
    };

    const handleSubscribeClick = () => {
        // Navigate to subscribe page with test results
        navigate('/subscribe', {
            state: {
                testResults: testResults,
                fromResults: true
            }
        });
    };

    // Loading State
    if (loading) {
        return (
            <div className="level-test-results-page">
                <NavComponent />
                <div className="results-loading">
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                    <p>{t('levelTestResults.loading')}</p>
                </div>
                <Footer />
            </div>
        );
    }

    // Error State - No Results
    if (!testResults) {
        return (
            <div className="level-test-results-page">
                <NavComponent />
                <div className="results-error-state">
                    <div className="results-error-content">
                        <i className="ti-clipboard results-error-icon info"></i>
                        <h2>{t('levelTestResults.noResultsTitle')}</h2>
                        <p>{t('levelTestResults.noResultsMessage')}</p>
                        <div className="results-error-actions">
                            <Link to="/level-test" className="theme-btn">
                                {t('levelTestResults.takeLevelTest')}
                            </Link>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="level-test-results-page">
            <NavComponent />

            {/* Hero Section with Trophy */}
            <div className="results-hero-section">
                <div className="container">
                    <div className="results-hero-content">
                        <div className="trophy-icon">
                            <FaTrophy />
                        </div>
                        <h1>{t('levelTestResults.congratulations')}</h1>
                        <p>{t('levelTestResults.completedMessage')}</p>
                    </div>
                </div>
            </div>

            {/* Results Stats Section */}
            <div className="results-stats-section">
                <div className="container">
                    {error && (
                        <div className="alert alert-warning results-alert">
                            <FaCheckCircle /> {error}
                        </div>
                    )}

                    <div className="row justify-content-center">
                        <div className="col-lg-6 col-md-8">
                            <div className="result-stat-card level-card">
                                <div className="stat-icon">
                                    <FaClipboardCheck />
                                </div>
                                <div className="stat-content">
                                    <div className="stat-label">{t('levelTestResults.proficiencyLevel')}</div>
                                    <div
                                        className="stat-value level-value"
                                        style={{ color: getLevelColor(testResults.level_result || testResults.level) }}
                                    >
                                        {testResults.level_result || testResults.level}
                                    </div>
                                    <div className="stat-meta">
                                        <div className="test-date">
                                            {t('levelTestResults.completedOn')} {new Date(testResults.test_date || testResults.created_at || Date.now()).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Guest Signup CTA - Only show if not authenticated */}
            {!isAuthenticated && (
                <section className="results-signup-section section-padding">
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-lg-8">
                                <div className="signup-cta-card">
                                    <div className="signup-cta-icon">
                                        <FaUserPlus />
                                    </div>
                                    <h2>{t('levelTestResults.getFullResults')}</h2>
                                    <p>{t('levelTestResults.signupDesc')}</p>

                                    <div className="signup-benefits">
                                        <div className="benefit-item">
                                            <FaEnvelope />
                                            <span>{t('levelTestResults.benefitEmail')}</span>
                                        </div>
                                        <div className="benefit-item">
                                            <FaWhatsapp />
                                            <span>{t('levelTestResults.benefitWhatsapp')}</span>
                                        </div>
                                        <div className="benefit-item">
                                            <FaGift />
                                            <span>{t('levelTestResults.benefitDemo')}</span>
                                        </div>
                                    </div>

                                    <div className="signup-cta-actions">
                                        <Link
                                            to="/signup"
                                            state={{
                                                from: '/level-test/results',
                                                action: 'level-test-results',
                                                testResults: testResults
                                            }}
                                            className="theme-btn"
                                        >
                                            {t('levelTestResults.createFreeAccount')} <FaArrowRight />
                                        </Link>
                                        <p className="signin-link">
                                            {t('levelTestResults.alreadyHaveAccount')} <Link to="/login" state={{ from: '/level-test/results' }}>{t('levelTestResults.signIn')}</Link>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )
            }

            {/* Subscription Options Section - Only show if authenticated */}
            {
                isAuthenticated && (
                    <section className="results-opportunity-section section-padding">
                        <div className="container">
                            <div className="row align-items-center">
                                <div className="col-xl-4 col-lg-12">
                                    <div className="section-title-area">
                                        <span className="section-subtitle">{t('levelTestResults.yourNextStep')}</span>
                                        <h3 className="section-title-main">
                                            {isDemoSubscription ? t('levelTestResults.upgradeToPremium') : t('levelTestResults.chooseLearningPath')}
                                        </h3>
                                        <p className="section-description">
                                            {isDemoSubscription
                                                ? t('levelTestResults.upgradeDesc')
                                                : t('levelTestResults.choosePathDesc')
                                            }
                                        </p>
                                    </div>
                                </div>
                                <div className="col-xl-8 col-lg-12">
                                    <div className="row">
                                        {/* Premium Subscription Card */}
                                        <div className={`${isDemoSubscription ? 'col-lg-12' : 'col-lg-6'} col-md-6 col-12`}>
                                            <div className="opportunity-card premium-card">
                                                <div className="card-badge">
                                                    <FaCrown /> {t('levelTestResults.bestValue')}
                                                </div>
                                                <h3>{t('levelTestResults.unlockAccess')}</h3>
                                                <div className="pricing-section">
                                                    <div className="savings-badge">
                                                        {t('levelTestResults.enrollNowFreeMonths')}
                                                    </div>
                                                    <div className="old-price">{t('levelTestResults.oldPrice')}: $59</div>
                                                    <div className="current-price">{t('levelTestResults.newPrice')}: $49</div>
                                                </div>
                                                <p className="card-description">
                                                    {t('levelTestResults.startLearningToday')}
                                                </p>
                                                <div className="card-image">
                                                    <img src={img1} alt="Premium Subscription" loading="lazy" decoding="async" />
                                                </div>
                                                <div className="card-action">
                                                    <button
                                                        onClick={handleSubscribeClick}
                                                        className="theme-btn"
                                                    >
                                                        {t('levelTestResults.proceedToPayment')} <FaArrowRight />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Free Demo Card - Only show if user doesn't have demo subscription */}
                                        {!isDemoSubscription && (
                                            <div className="col-lg-6 col-md-6 col-12">
                                                <div className="opportunity-card demo-card">
                                                    <div className="card-badge demo-badge">
                                                        <FaGift /> {t('levelTestResults.freeTrial')}
                                                    </div>
                                                    <h3>{t('levelTestResults.unlockAccess2Weeks')}</h3>
                                                    <div className="pricing-section">
                                                        <div className="current-price free-price">{t('levelTestResults.explorePlatform')}</div>
                                                    </div>
                                                    <p className="card-description">
                                                        {t('levelTestResults.access2Lessons')}<br />
                                                        {t('levelTestResults.noCreditCard')}
                                                    </p>
                                                    <div className="card-image">
                                                        <img src={img3} alt="Free Demo" loading="lazy" decoding="async" />
                                                    </div>
                                                    <div className="card-action">
                                                        <button
                                                            onClick={handleDemoClick}
                                                            className="theme-btn-s2"
                                                        >
                                                            {t('levelTestResults.proceedToDemo')} <FaArrowRight />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )
            }

            <Footer />
            <Scrollbar />
        </div >
    );
};

export default LevelTestResultsPage;
